/**
 * MicrophonePermissionScreen Component
 * 
 * User-friendly screen shown when microphone permission is denied.
 * Provides clear instructions and option to continue in keyboard mode.
 */

import { useTranslation } from 'react-i18next';
import { useState } from 'react';

interface MicrophonePermissionScreenProps {
  onContinueKeyboard?: () => void;  // If provided, shows "Continue in text mode" button
  onRetry: () => void;
  onCancel: () => void;
  allowKeyboardInput: boolean;
}

// Helper to detect browser type
function detectBrowser(): 'chrome' | 'safari' | 'firefox' | 'edge' | 'other' {
  const ua = navigator.userAgent.toLowerCase();
  
  if (ua.includes('edg/') || ua.includes('edge')) return 'edge';
  if (ua.includes('chrome') && !ua.includes('edg')) return 'chrome';
  if (ua.includes('safari') && !ua.includes('chrome')) return 'safari';
  if (ua.includes('firefox')) return 'firefox';
  return 'other';
}

// Helper to detect if mobile
function isMobile(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// Helper to detect if iOS
function isIOS(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

// Helper to detect if Android
function isAndroid(): boolean {
  return /Android/i.test(navigator.userAgent);
}

export default function MicrophonePermissionScreen({
  onContinueKeyboard,
  onRetry,
  onCancel,
  allowKeyboardInput
}: MicrophonePermissionScreenProps) {
  const { t } = useTranslation();
  const [showInstructions, setShowInstructions] = useState(!allowKeyboardInput);
  
  const browser = detectBrowser();
  const mobile = isMobile();
  const ios = isIOS();
  const android = isAndroid();
  
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        
        {/* Icon - Microphone with slash */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-100 rounded-full mb-4">
            <svg className="w-10 h-10 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" />
            </svg>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('errors.micPermission.title', 'Microphone Access Required')}
          </h2>
          
          <p className="text-gray-600 text-base">
            {t('errors.micPermission.description', "We couldn't access your microphone. This usually happens when permission was denied or blocked.")}
          </p>
        </div>
        
        {/* Main Actions */}
        <div className="space-y-3 mb-6">
          {/* Primary: Continue in Text Mode (if allowed) */}
          {allowKeyboardInput && onContinueKeyboard && (
            <button
              onClick={onContinueKeyboard}
              className="w-full px-6 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors font-medium text-lg flex items-center justify-center gap-3 shadow-lg"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <line x1="6" y1="9" x2="6.01" y2="9" strokeWidth="2" strokeLinecap="round" />
                <line x1="10" y1="9" x2="10.01" y2="9" strokeWidth="2" strokeLinecap="round" />
                <line x1="14" y1="9" x2="14.01" y2="9" strokeWidth="2" strokeLinecap="round" />
                <line x1="18" y1="9" x2="18.01" y2="9" strokeWidth="2" strokeLinecap="round" />
                <line x1="8" y1="13" x2="16" y2="13" strokeWidth="2" strokeLinecap="round" />
              </svg>
              {t('errors.micPermission.continueTyping', 'Continue by Typing')}
            </button>
          )}
          
          {/* Secondary: Show/Hide Instructions */}
          {allowKeyboardInput && (
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 active:bg-gray-300 transition-colors font-medium flex items-center justify-center gap-2"
            >
              <svg className={`w-5 h-5 transition-transform ${showInstructions ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
              {showInstructions 
                ? t('errors.micPermission.hideInstructions', 'Hide microphone instructions')
                : t('errors.micPermission.showInstructions', 'I want to use voice instead')
              }
            </button>
          )}
        </div>
        
        {/* Instructions Panel (expandable or always visible) */}
        {showInstructions && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <span>🎤</span>
              {t('errors.micPermission.howToEnable', 'How to enable microphone:')}
            </h3>
            
            {/* Mobile Instructions */}
            {mobile && (
              <div className="space-y-3 text-sm text-blue-800">
                {android && (
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-green-600">Android:</span>
                    <span>{t('errors.micPermission.androidSteps', 'Go to Settings → Apps → Browser → Permissions → Microphone → Allow')}</span>
                  </div>
                )}
                {ios && (
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-gray-600">iPhone/iPad:</span>
                    <span>{t('errors.micPermission.iosSteps', 'Go to Settings → Safari → Microphone → Allow')}</span>
                  </div>
                )}
                <div className="text-blue-700 font-medium pt-2 border-t border-blue-200">
                  {t('errors.micPermission.reloadAfter', 'After changing settings, reload this page')}
                </div>
              </div>
            )}
            
            {/* Desktop Instructions */}
            {!mobile && (
              <div className="space-y-3 text-sm text-blue-800">
                {(browser === 'chrome' || browser === 'edge') && (
                  <div className="flex items-start gap-2">
                    <span className="text-lg">🔒</span>
                    <span>{t('errors.micPermission.chromeSteps', 'Click the lock icon in the address bar → Site settings → Microphone → Allow')}</span>
                  </div>
                )}
                {browser === 'safari' && (
                  <div className="flex items-start gap-2">
                    <span className="text-lg">🍎</span>
                    <span>{t('errors.micPermission.safariSteps', 'Safari menu → Settings for This Website → Microphone → Allow')}</span>
                  </div>
                )}
                {browser === 'firefox' && (
                  <div className="flex items-start gap-2">
                    <span className="text-lg">🦊</span>
                    <span>{t('errors.micPermission.firefoxSteps', 'Click the permissions icon → Microphone → Allow')}</span>
                  </div>
                )}
                {browser === 'other' && (
                  <div className="text-blue-700">
                    {t('errors.micPermission.genericSteps', 'Check your browser settings to allow microphone access for this site')}
                  </div>
                )}
                <div className="text-blue-700 font-medium pt-2 border-t border-blue-200">
                  {t('errors.micPermission.reloadAfter', 'After changing settings, reload this page')}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Bottom Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onRetry}
            className={`flex-1 px-6 py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
              allowKeyboardInput 
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {t('common.buttons.retry', 'Try Again')}
          </button>
          
          <button
            onClick={onCancel}
            className="flex-1 px-6 py-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors font-medium"
          >
            {t('common.buttons.back', 'Go Back')}
          </button>
        </div>
        
        {/* Help text */}
        {!allowKeyboardInput && (
          <p className="text-center text-sm text-gray-500 mt-4">
            {t('errors.micPermission.voiceRequired', 'This experience requires voice input. Please enable microphone access to continue.')}
          </p>
        )}
      </div>
    </div>
  );
}
